export type SoundfontSource =
  | { kind: "catalog"; id: string; url: string; label: string }
  | { kind: "url"; url: string }
  | { kind: "file"; url: string; name: string };

export const isSoundfontSource = (value: unknown): value is SoundfontSource => {
  if (!value || typeof value !== "object") return false;
  const v = value as { kind?: unknown; url?: unknown };
  if (typeof v.url !== "string" || v.url.length === 0) return false;
  return v.kind === "catalog" || v.kind === "url" || v.kind === "file";
};

/**
 * Человекочитаемое наименование источника SF2 для UI
 * (`catalog → "Каталог: label"`, `file → "Файл: name"`, `url → "URL: url"`).
 */
export const describeSoundfontSource = (source: SoundfontSource): string => {
  if (source.kind === "catalog") return `Каталог: ${source.label}`;
  if (source.kind === "file") return `Файл: ${source.name}`;
  return `URL: ${source.url}`;
};

/** Прогресс загрузки байтов SF2. `total` может быть `null` (нет Content-Length). */
export type SoundfontLoadProgress = {
  readonly loaded: number;
  readonly total: number | null;
};

type ProgressListener = (p: SoundfontLoadProgress) => void;

type CacheEntry = {
  promise: Promise<Uint8Array>;
  /** Слушатели прогресса загрузки этой записи (подписчики из разных движков/UI). */
  listeners: Set<ProgressListener>;
};

// Модульный кэш байтов SF2 по `source.url`. Позволяет дедуплицировать сетевые
// загрузки между предзагрузкой UI, `useProgramMap` и движками (`TimelinePlayer`,
// `PatternMiniPlayer`): все потребители одного URL разделяют один fetch.
// `listeners` — Set, потому что fetch может начать любой консьюмер (эффекты
// React выполняются child-first — движок раньше предзагрузки App), а оверлей
// должен получать прогресс независимо от того, кто инициировал загрузку.
const cache = new Map<string, CacheEntry>();

const notify = (
  listeners: ReadonlySet<ProgressListener>,
  progress: SoundfontLoadProgress
): void => {
  for (const listener of listeners) listener(progress);
};

const nowMs = (): number =>
  typeof performance !== "undefined" ? performance.now() : Date.now();

const NOTIFY_INTERVAL_MS = 100;

/**
 * Скачивает байты SF2 по URL со стриминговым прогрессом. При отсутствии
 * `res.body` (blob:/file: URL, стабы в тестах) — fallback на `arrayBuffer()`
 * с мгновенным 100%.
 */
const fetchBytes = async (
  url: string,
  listeners: ReadonlySet<ProgressListener>
): Promise<Uint8Array> => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status} при загрузке ${url}`);
  const contentLength = res.headers?.get?.("content-length");
  const total =
    typeof contentLength === "string" && contentLength.length > 0
      ? Number.parseInt(contentLength, 10) || null
      : null;
  const body = res.body;
  if (!body) {
    const data = new Uint8Array(await res.arrayBuffer());
    notify(listeners, { loaded: data.byteLength, total: total ?? data.byteLength });
    return data;
  }
  const reader = body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;
  let lastNotify = 0;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) {
      chunks.push(value);
      loaded += value.byteLength;
      const t = nowMs();
      if (t - lastNotify >= NOTIFY_INTERVAL_MS) {
        lastNotify = t;
        notify(listeners, { loaded, total });
      }
    }
  }
  notify(listeners, { loaded, total });
  const out = new Uint8Array(loaded);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return out;
};

/**
 * Загружает сырые байты SF2 по `SoundfontSource` (catalog/url/file — все
 * используют `fetch(source.url)`). Результат кэшируется по `source.url`:
 * повторные вызовы (движки, `useProgramMap`, предзагрузка UI) разделяют один
 * fetch, без дублирования сетевого трафика. `onProgress` — опциональный
 * слушатель прогресса (получает `SoundfontLoadProgress`).
 *
 * Бросает `Error` при сетевой ошибке или не-HTTP-ответе. При ошибке запись
 * кэша эвиктится — следующий вызов повторит fetch.
 */
export const loadSoundfontBytes = async (
  source: SoundfontSource,
  onProgress?: ProgressListener
): Promise<Uint8Array> => {
  const key = source.url;
  const existing = cache.get(key);
  if (existing) {
    if (onProgress) {
      existing.listeners.add(onProgress);
      existing.promise.finally(() => existing.listeners.delete(onProgress)).catch(() => undefined);
    }
    return existing.promise;
  }

  const listeners = new Set<ProgressListener>();
  if (onProgress) listeners.add(onProgress);
  const promise = fetchBytes(source.url, listeners);
  cache.set(key, { promise, listeners });
  if (onProgress) {
    promise
      .then(() => listeners.delete(onProgress))
      .catch(() => listeners.delete(onProgress));
  }
  try {
    return await promise;
  } catch (err) {
    if (cache.get(key)?.promise === promise) cache.delete(key);
    throw err;
  }
};

/**
 * Очищает кэш байтов SF2. Вызывается после того, как новый шрифт полностью
 * загружен (App-предзагрузка), чтобы старые байты не держались в памяти;
 * `keepUrl` оставляет в кэше текущий источник. Также используется тестами.
 */
export const clearSoundfontCache = (keepUrl?: string): void => {
  for (const key of Array.from(cache.keys())) {
    if (keepUrl !== undefined && key === keepUrl) continue;
    cache.delete(key);
  }
};
